<!-- ═══════════════════════════════════════════════════════════════════════════
     Chareety — Marketing Homepage
     Professional nonprofit SaaS landing page
     ═══════════════════════════════════════════════════════════════════════════ -->

<!-- ── Hero ─────────────────────────────────────────────────────────────────── -->
<section class="hero">
  <div class="hero-inner">
    <div class="hero-content">
      <span class="hero-badge">Nonprofit Donation Platform</span>
      <h1>Accept donations.<br>Grow your mission.</h1>
      <p class="hero-sub">
        Chareety provides an all-in-one platform to seamlessly collect donations
        in person and online. From stationary kiosks and mobile giving pages to comprehensive donor
        management and automated tax receipts—get set up in minutes, not months.
      </p>
      <div class="hero-actions">
        <button mat-flat-button class="btn-cta" (click)="navigateToOnboard()">
          Start Free Onboarding
        </button>
        <a mat-stroked-button class="btn-secondary" href="#features">
          Learn More
        </a>
      </div>
      <p class="hero-note">No credit card required. 100% free to get started.</p>
    </div>
    <div class="hero-visual">
      <div class="hero-card">
        <div class="hero-card-header">
          <mat-icon>volunteer_activism</mat-icon>
          <span>Recent Donation</span>
        </div>
        <div class="hero-card-amount">$250.00</div>
        <div class="hero-card-detail">Sarah M. — General Fund</div>
        <div class="hero-card-status">
          <span class="status-dot"></span> Receipt sent automatically
        </div>
      </div>
    </div>
  </div>
</section>

<!-- ── Trusted By (social proof strip) ─────────────────────────────────────── -->
<section class="trust-strip">
  <div class="trust-inner">
    <p>Trusted by mosques, churches, temples, food banks, and community nonprofits everywhere</p>
  </div>
</section>

<!-- ── Features ─────────────────────────────────────────────────────────────── -->
<section class="features" id="features">
  <div class="section-inner">
    <h2 class="section-heading">Everything you need to collect donations</h2>
    <p class="section-sub">
      From in-person kiosks to custom online giving pages, Chareety handles the entire
      donation lifecycle so you can focus on growing your mission.
    </p>

    <div class="features-grid">
      <div class="feature-card">
        <div class="feature-icon orange">
          <mat-icon>shopping_bag</mat-icon>
        </div>
        <h3>Mobile Box (Patent Pending)</h3>
        <p>
          Portable donation terminal for collecting cash and digital payments anywhere—farmers markets, events, or outside venues. Perfect for fundraising on the go.
        </p>
      </div>

      <div class="feature-card">
        <div class="feature-icon blue">
          <mat-icon>point_of_sale</mat-icon>
        </div>
        <h3>Stationary Kiosk Donations</h3>
        <p>
          Tablet-based kiosks for fixed locations. Donors tap or insert cash, select their amount, and give instantly—no app required.
        </p>
      </div>

      <div class="feature-card">
        <div class="feature-icon blue">
          <mat-icon>smartphone</mat-icon>
        </div>
        <h3>BYOD Mobile App</h3>
        <p>
          Turn your smartphone into a powerful donation terminal. Perfect for off-site events or busy days—simple, portable, and requires no additional hardware.
        </p>
      </div>

      <div class="feature-card">
        <div class="feature-icon orange">
          <mat-icon>phone_iphone</mat-icon>
        </div>
        <h3>Mobile Giving Pages</h3>
        <p>
          Branded giving page at your own subdomain. Share via QR code, email, or social media.
        </p>
      </div>

      <div class="feature-card">
        <div class="feature-icon blue">
          <mat-icon>groups</mat-icon>
        </div>
        <h3>Donor Management</h3>
        <p>
          Track donors, view giving history, manage subscriptions, and segment your supporter base.
        </p>
      </div>

      <div class="feature-card">
        <div class="feature-icon orange">
          <mat-icon>receipt_long</mat-icon>
        </div>
        <h3>Digital Receipts & Tax Documents</h3>
        <p>
          Automatic IRS-compliant receipts sent instantly to donors. Year-end tax statements generated automatically.
        </p>
      </div>

      <div class="feature-card">
        <div class="feature-icon orange">
          <mat-icon>campaign</mat-icon>
        </div>
        <h3>Email Broadcasts</h3>
        <p>
          Send targeted campaigns to donors with built-in Brevo integration. Share updates and requests easily.
        </p>
      </div>

      <div class="feature-card">
        <div class="feature-icon blue">
          <mat-icon>security</mat-icon>
        </div>
        <h3>Secure, Stripe-Powered Payments</h3>
        <p>
          Enterprise-grade, PCI-compliant card processing via Stripe Connect. Funds are deposited directly and securely into your organization's bank account.
        </p>
      </div>

      <div class="feature-card">
        <div class="feature-icon orange">
          <mat-icon>handshake</mat-icon>
        </div>
        <h3>Pledge Management</h3>
        <p>
          Donors can pledge support without committing funds immediately. Organizations send automated reminders with donation links so donors contribute when ready—at their own pace.
        </p>
      </div>
    </div>
  </div>
</section>

<!-- ── How It Works ─────────────────────────────────────────────────────────── -->
<section class="how-it-works">
  <div class="section-inner">
    <h2 class="section-heading">Get started in three steps</h2>
    <p class="section-sub">
      Our guided onboarding walks you through everything. Most organizations
      are up and running the same day.
    </p>

    <div class="steps-grid">
      <div class="step-card">
        <div class="step-number">1</div>
        <h3>Register your organization</h3>
        <p>
          Enter your nonprofit's name, EIN, and contact details.
          We verify your tax-exempt status automatically.
        </p>
      </div>

      <div class="step-connector">
        <mat-icon>arrow_forward</mat-icon>
      </div>

      <div class="step-card">
        <div class="step-number">2</div>
        <h3>Connect payments &amp; communication</h3>
        <p>
          Securely link your bank account via Stripe Express and configure
          our Brevo integration for automated donor receipts and outreach.
        </p>
      </div>

      <div class="step-connector">
        <mat-icon>arrow_forward</mat-icon>
      </div>

      <div class="step-card">
        <div class="step-number">3</div>
        <h3>Start accepting donations</h3>
        <p>
          Your branded giving page and admin console are ready.
          Share your link or set up a kiosk — donations flow immediately.
        </p>
      </div>
    </div>
  </div>
</section>

<!-- ── Pricing ──────────────────────────────────────────────────────────────── -->
<section class="pricing" id="pricing">
  <div class="section-inner">
    <h2 class="section-heading">Flexible Pricing Built to Scale With You</h2>
    <p class="section-sub">
      Simple, transparent rates with no long-term commitments. Pay only for what you use—no base fees, no surprises.
    </p>

    <div class="pricing-table-wrapper">
      <table class="pricing-comparison-table">
        <thead>
          <tr>
            <th class="feature-col">Feature</th>
            <th class="plan-col">Free Plan<br><span class="subtext">No CC Needed</span></th>
            <th class="plan-col featured">Premium<br><span class="subtext">Pay-As-You-Go</span></th>
            <th class="plan-col">Enterprise<br><span class="subtext">Bespoke</span></th>
          </tr>
        </thead>
        <tbody>
          <!-- Donation Management -->
          <tr class="category-row">
            <td colspan="4">Donation Management</td>
          </tr>
          <tr>
            <td class="feature-name">Full Dashboard Access</td>
            <td><mat-icon class="check">check_circle</mat-icon></td>
            <td><mat-icon class="check">check_circle</mat-icon></td>
            <td><mat-icon class="check">check_circle</mat-icon></td>
          </tr>

          <!-- Receipts & Statements -->
          <tr class="category-row">
            <td colspan="4">Receipts & Statements</td>
          </tr>
          <tr>
            <td class="feature-name">SMS Receipts & Tax Docs</td>
            <td><mat-icon class="check">check_circle</mat-icon></td>
            <td><mat-icon class="check">check_circle</mat-icon></td>
            <td><mat-icon class="check">check_circle</mat-icon></td>
          </tr>

          <!-- Subscriptions & Support -->
          <tr class="category-row">
            <td colspan="4">Subscriptions & Support</td>
          </tr>
          <tr>
            <td class="feature-name">Unlimited Support</td>
            <td><mat-icon class="check">check_circle</mat-icon></td>
            <td><mat-icon class="check">check_circle</mat-icon></td>
            <td><mat-icon class="check">check_circle</mat-icon></td>
          </tr>

          <!-- Payment Processing -->
          <tr class="category-row">
            <td colspan="4">Payment Processing</td>
          </tr>
          <tr>
            <td class="feature-name">Cash/Check/Zelle</td>
            <td class="pricing-detail">First 200 Free/mo</td>
            <td class="pricing-detail">First 200 Free, then $0.10</td>
            <td class="pricing-detail">Unlimited</td>
          </tr>
          <tr>
            <td class="feature-name">Subscription Processing</td>
            <td class="pricing-detail">Stripe Fees + 1%</td>
            <td class="pricing-detail">Stripe Fees + 1%</td>
            <td class="pricing-detail">Contact Sales</td>
          </tr>
          <tr>
            <td class="feature-name">One-Time Digital Gifts</td>
            <td class="pricing-detail">Stripe Fees + 0.5% + $0.10</td>
            <td class="pricing-detail">Stripe Fees + 0.5% + $0.10</td>
            <td class="pricing-detail">Contact Sales</td>
          </tr>

          <!-- Communications -->
          <tr class="category-row">
            <td colspan="4">Communications</td>
          </tr>
          <tr>
            <td class="feature-name">Communications</td>
            <td class="pricing-detail">First 200 Free/mo</td>
            <td class="pricing-detail">First 200 Free, then $0.005</td>
            <td class="pricing-detail">Unlimited</td>
          </tr>
          <tr>
            <td class="feature-name" colspan="4" style="font-size: 0.85em; color: #666; padding-top: 0;">
              Includes broadcasts, reminders, re-sends, tax documents. First donation receipt is always free.
            </td>
          </tr>

          <!-- Digital Platforms -->
          <tr class="category-row">
            <td colspan="4">Digital Platforms</td>
          </tr>
          <tr>
            <td class="feature-name">BYOD Phone App</td>
            <td class="pricing-detail">Available ($0 Fees)</td>
            <td class="pricing-detail">Available ($0 Fees)</td>
            <td class="pricing-detail">Available ($0 Fees)</td>
          </tr>

          <!-- Hardware & Devices -->
          <tr class="category-row">
            <td colspan="4">Hardware & Devices</td>
          </tr>
          <tr>
            <td class="feature-name">1st Kiosk / Mobile Box (patent pending)</td>
            <td class="pricing-detail">$499 ($399 Intro Offer)</td>
            <td class="pricing-detail">$499 ($399 Intro Offer)</td>
            <td class="pricing-detail">$499 ($399 Intro Offer)</td>
          </tr>
          <tr>
            <td class="feature-name">Additional Devices</td>
            <td class="pricing-detail">$299 per unit</td>
            <td class="pricing-detail">$299 per unit</td>
            <td class="pricing-detail">$299 per unit</td>
          </tr>
          <tr>
            <td class="feature-name">On-Site Setup Fee</td>
            <td class="pricing-detail">$100 per visit</td>
            <td class="pricing-detail">$100 per visit</td>
            <td class="pricing-detail">$100 per visit</td>
          </tr>
          <tr>
            <td class="feature-name">SIM Connectivity</td>
            <td class="pricing-detail">Not Available</td>
            <td class="pricing-detail">$10/month per device</td>
            <td class="pricing-detail">$10/month per device</td>
          </tr>

          <!-- Customization -->
          <tr class="category-row">
            <td colspan="4">Customization</td>
          </tr>
          <tr>
            <td class="feature-name">Donor Mobile App</td>
            <td class="pricing-detail">Chareety Branded</td>
            <td class="pricing-detail">Chareety Branded</td>
            <td class="pricing-detail">Full White-Labeling</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="pricing-actions">
      <button mat-flat-button class="btn-cta" (click)="navigateToOnboard()">
        Start Free Onboarding
      </button>
      <button mat-stroked-button class="btn-secondary" (click)="scrollToContact()">
        Contact Sales for Enterprise
      </button>
    </div>
  </div>
</section>

<!-- ── Why Switch to Chareety ──────────────────────────────────────────────── -->
<section class="why-switch" id="compare">
  <div class="section-inner">
    <h2 class="section-heading">Why Leading Nonprofits Choose Chareety</h2>
    <p class="section-sub">
      Built by nonprofits, for nonprofits. Pay-as-you-grow pricing means your costs scale with your impact, not the other way around.
    </p>

    <div class="why-switch-benefits" style="display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 32px; margin-bottom: 48px;">
      <!-- Advantage 1 -->
      <div class="advantage-card">
        <div class="advantage-icon">
          <mat-icon>devices</mat-icon>
        </div>
        <h3>Purpose-Built Hardware</h3>
        <p>
          Professional-grade hardware that's affordable and yours to keep. One-time $399 investment with <strong>zero</strong> $80+/month recurring device fees.
        </p>
      </div>

      <!-- Advantage 2 -->
      <div class="advantage-card">
        <div class="advantage-icon">
          <mat-icon>card_giftcard</mat-icon>
        </div>
        <h3>Start Free, No Commitment</h3>
        <p>
          No credit card needed. Get 200 free monthly offline records, full donor dashboard access, and robust management tools instantly.
        </p>
      </div>

      <!-- Advantage 3 -->
      <div class="advantage-card">
        <div class="advantage-icon">
          <mat-icon>trending_up</mat-icon>
        </div>
        <h3>Honest, Transparent Pricing</h3>
        <p>
          Flat 0.5% + $0.10 on digital gifts. Tipping is always 100% optional for donors—no hidden service charges or penalty fees for you.
        </p>
      </div>
    </div>

    <!-- Main Comparison Card -->
    <div class="advantage-card featured-compare-card" style="max-width: 900px; margin: 0 auto; padding: 40px; text-align: center;">

      <h3 style="font-size: 1.8rem; margin-bottom: 16px;">See How We Compare</h3>
      <p style="margin-bottom: 32px; max-width: 700px; margin-left: auto; margin-right: auto;">
        See how Chareety stacks up against leading nonprofit platforms on the features that matter most to your bottom line. We prioritize your mission over our margins.
      </p>
      
      <div style="overflow-x: auto; text-align: left;">
        <table class="comparison-table" style="width: 100%; min-width: 600px;">
          <thead>
            <tr>
              <th>Feature</th>
              <th style="background-color: #fff3e0; color: #e65100; font-size: 1.1em; border-top-left-radius: 8px; border-top-right-radius: 8px;">Chareety</th>
              <th>Donorbox</th>
              <th>Givebutter &amp; Zeffy</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Dedicated Kiosk Hardware</strong></td>
              <td style="background-color: #fff3e0; color: #e65100; font-weight: 700;">Included ($399)</td>
              <td>iPad Only ($349) + CC terminal ($60)</td>
              <td>No Dedicated Kiosk</td>
            </tr>
            <tr>
              <td><strong>Additional Kiosks</strong></td>
              <td style="background-color: #fff3e0; color: #e65100; font-weight: 700;">$299 per device</td>
              <td>Full Price ($409+)</td>
              <td>N/A</td>
            </tr>
            <tr>
              <td><strong>Mobile Box (Portable)</strong></td>
              <td style="background-color: #fff3e0; color: #e65100; font-weight: 700;">Yes (Exclusive)</td>
              <td>No</td>
              <td>No</td>
            </tr>
            <tr>
              <td><strong>Monthly Device Fee</strong></td>
              <td style="background-color: #fff3e0; color: #e65100; font-weight: 700;">$0</td>
              <td>$80/mo per device</td>
              <td>N/A</td>
            </tr>
            <tr>
              <td><strong>Digital Donations Fee</strong></td>
              <td style="background-color: #fff3e0; color: #e65100; font-weight: 700;">0.5% + $0.10 + Stripe</td>
              <td>1.75% + Stripe</td>
              <td>Forced Tips *</td>
            </tr>
            <tr>
              <td><strong>Recurring Donations Fee</strong></td>
              <td style="background-color: #fff3e0; color: #e65100; font-weight: 700;">1% + Stripe</td>
              <td>1.75% + Stripe</td>
              <td>Forced Tips *</td>
            </tr>
            <tr>
              <td><strong>Offline Records</strong></td>
              <td style="background-color: #fff3e0; color: #e65100; font-weight: 700;">200 Free/mo</td>
              <td>Gated ($500+ volume)</td>
              <td>Included</td>
            </tr>
            <tr>
              <td><strong>Setup &amp; Support</strong></td>
              <td style="background-color: #fff3e0; color: #e65100; font-weight: 700; border-bottom-left-radius: 8px; border-bottom-right-radius: 8px;">Optional On-Site</td>
              <td>Remote Only</td>
              <td>Remote Only</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p class="comparison-note" style="margin-top: 24px; font-size: 0.9rem;">
        * Givebutter and Zeffy apply fees if donor tips are disabled or strongly prompt donors to "tip" the platform, which can confuse supporters.
      </p>
      <p style="margin-top: 24px; font-size: 1.1rem; color: #e65100;">
        <strong>We're more than a payment processor. We're your nonprofit's growth platform.</strong>
      </p>
    </div>
  </div>
</section>

<!-- ── Cost Calculator ─────────────────────────────────────────────────────── -->
<app-cost-calculator></app-cost-calculator>

<!-- ── Testimonials ─────────────────────────────────────────────────────────── -->
<section class="testimonials">
  <div class="section-inner">
    <h2 class="section-heading">Trusted by organizations like yours</h2>

    <div class="testimonials-grid">
      <div class="testimonial-card">
        <p class="testimonial-quote">
          "Chareety replaced three different tools we were using for donations,
          receipts, and donor tracking. The kiosk feature alone has doubled our
          in-person giving."
        </p>
        <div class="testimonial-author">
          <div class="author-avatar">AH</div>
          <div>
            <div class="author-name">Ahmed H.</div>
            <div class="author-role">Treasurer, Community Mosque</div>
          </div>
        </div>
      </div>

      <div class="testimonial-card">
        <p class="testimonial-quote">
          "Setup took us less than a day. We were accepting online donations
          by the evening. The automatic receipts have saved us hours every week."
        </p>
        <div class="testimonial-author">
          <div class="author-avatar">MR</div>
          <div>
            <div class="author-name">Maria R.</div>
            <div class="author-role">Director, Local Food Bank</div>
          </div>
        </div>
      </div>

      <div class="testimonial-card">
        <p class="testimonial-quote">
          "The donor management dashboard gives us a clear picture of our
          supporter base. We can finally see trends and plan our outreach."
        </p>
        <div class="testimonial-author">
          <div class="author-avatar">JP</div>
          <div>
            <div class="author-name">James P.</div>
            <div class="author-role">Pastor, Grace Church</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- ── FAQ ──────────────────────────────────────────────────────────────────── -->
<section class="faq" id="faq">
  <div class="section-inner">
    <h2 class="section-heading">Frequently asked questions</h2>

    <div class="faq-list">
      <div class="faq-item" *ngFor="let item of faqItems; let i = index">
        <button class="faq-question" (click)="toggleFaq(i)">
          <span>{{ item.question }}</span>
          <mat-icon>{{ openFaqIndex === i ? 'expand_less' : 'expand_more' }}</mat-icon>
        </button>
        <div class="faq-answer" *ngIf="openFaqIndex === i">
          <p>{{ item.answer }}</p>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- ── Bottom CTA ──────────────────────────────────────────────────────────── -->
<section class="bottom-cta">
  <div class="section-inner">
    <h2>Ready to transform your nonprofit's giving?</h2>
    <p>Join hundreds of organizations raising more with less friction, starting today.</p>
    <button mat-flat-button class="btn-cta btn-cta-lg" (click)="navigateToOnboard()">
      Start Free Onboarding
    </button>
  </div>
</section>

<!-- ── Contact ─────────────────────────────────────────────────────────────── -->
<div id="contact">
  <app-contact-us></app-contact-us>
</div>
